import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import EventNoteIcon from '@mui/icons-material/EventNote';
import GroupsIcon from '@mui/icons-material/Groups';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { format, parseISO } from 'date-fns';
import * as qs from 'qs';
import * as XLSX from 'xlsx';

import TableInfo from '../../components/TableInfo';
import api from '../../services/api';
import {
    getAllowedPortalEventIds,
    parsePermissions,
} from '../../utils/permissions';

const summaryCardSx = {
    borderRadius: 4,
    px: 3,
    py: 2.5,
    minHeight: 148,
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.14)',
};

function formatDate(value) {
    if (!value) return '-';

    try {
        return format(parseISO(value), 'dd/MM/yy');
    } catch (error) {
        return value;
    }
}

function buildSubscribersFromOrders(orders) {
    const uniqueSubscribers = new Map();

    orders.forEach((order) => {
        const eventAttributes = order.attributes?.event?.data?.attributes || {};
        const companyAttributes = order.attributes?.company?.data?.attributes || {};
        const subscribers = order.attributes?.subscribers?.data || [];

        subscribers.forEach((subscriber) => {
            if (uniqueSubscribers.has(subscriber.id)) return;

            uniqueSubscribers.set(subscriber.id, {
                id: subscriber.id,
                attributes: {
                    event: eventAttributes.name || '-',
                    company: companyAttributes.social_reason || 'Sem instituição',
                    locale: companyAttributes.city || subscriber.attributes?.city || '-',
                    uf: companyAttributes.state || subscriber.attributes?.state || '-',
                    name: subscriber.attributes?.name || '-',
                    badge: subscriber.attributes?.badge || '-',
                    email: subscriber.attributes?.email || '-',
                    whatsapp: subscriber.attributes?.whatsapp || '-',
                    document: subscriber.attributes?.document || '-',
                },
            });
        });
    });

    return Array.from(uniqueSubscribers.values()).sort((left, right) => (
        String(left.attributes.name).localeCompare(String(right.attributes.name), 'pt-BR')
    ));
}

async function fetchAllOrdersByEvent(eventId) {
    let currentPage = 1;
    let hasMore = true;
    const allOrders = [];

    while (hasMore) {
        const query = qs.stringify({
            sort: ['subscribers.name:asc'],
            pagination: { page: currentPage, pageSize: 100 },
            filters: {
                deleted: { $eq: false },
                event: { id: { $eq: eventId } },
            },
            populate: {
                event: { fields: ['name'] },
                company: { fields: ['social_reason', 'city', 'state'] },
                subscribers: {
                    fields: ['name', 'badge', 'document', 'whatsapp', 'email', 'city', 'state'],
                },
            },
            encodeValuesOnly: true,
        }, { encodeValuesOnly: true });

        const response = await api.get(`/orders?${query}`);
        const orders = response.data?.data || [];
        const pagination = response.data?.meta?.pagination;

        allOrders.push(...orders);

        if (!pagination || pagination.page >= pagination.pageCount) {
            hasMore = false;
        } else {
            currentPage += 1;
        }
    }

    return allOrders;
}

export default function PortalEventos() {
    const [events, setEvents] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [loadingSubscribers, setLoadingSubscribers] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [stateAlert, setStateAlert] = useState({ status: false, type: 'success', msg: '' });

    const permissions = useMemo(
        () => parsePermissions(sessionStorage.getItem('@EVAPI:permissions')),
        []
    );
    const allowedEventIds = useMemo(
        () => getAllowedPortalEventIds(permissions),
        [permissions]
    );

    const selectedEvent = useMemo(
        () => events.find((event) => event.id === selectedEventId) || null,
        [events, selectedEventId]
    );

    const eventColumns = [
        { Header: 'Evento', accessor: 'name', width: '30%' },
        { Header: 'Cidade', accessor: 'city', width: '16%' },
        { Header: 'Inscritos', accessor: 'count_subscribers', width: '12%' },
        { Header: 'Máximo', accessor: 'max_subscribers', width: '12%' },
        { Header: 'Início', accessor: 'date_initial', width: '12%' },
        { Header: 'Situação', accessor: 'isActive', width: '12%' },
    ];

    const subscriberColumns = [
        { Header: 'Instituição', accessor: 'company', width: '20%' },
        { Header: 'Cidade', accessor: 'locale', width: '14%' },
        { Header: 'UF', accessor: 'uf', width: '8%' },
        { Header: 'Nome', accessor: 'name', width: '18%' },
        { Header: 'Cracha', accessor: 'badge', width: '12%' },
        { Header: 'Email', accessor: 'email', width: '18%' },
        { Header: 'Whatsapp', accessor: 'whatsapp', width: '14%' },
        { Header: 'CPF', accessor: 'document', width: '12%' },
    ];

    useEffect(() => {
        async function loadAllowedEvents() {
            if (allowedEventIds.length === 0) {
                setEvents([]);
                setLoadingEvents(false);
                return;
            }

            try {
                const query = qs.stringify({
                    filters: {
                        deleted: { $eq: false },
                        id: { $in: allowedEventIds },
                    },
                    fields: ['name', 'city', 'count_subscribers', 'max_subscribers', 'date_initial', 'isActive'],
                    sort: ['date_initial:desc'],
                    pagination: { page: 1, pageSize: Math.max(allowedEventIds.length, 10) },
                    encodeValuesOnly: true,
                }, { encodeValuesOnly: true });

                const response = await api.get(`/events?${query}`);
                const nextEvents = (response.data?.data || []).map((event) => ({
                    ...event,
                    attributes: {
                        ...event.attributes,
                        city: event.attributes?.city || '-',
                        count_subscribers: `${event.attributes?.count_subscribers || 0}`,
                        max_subscribers: `${event.attributes?.max_subscribers || 0}`,
                        date_initial: formatDate(event.attributes?.date_initial),
                        isActive: event.attributes?.isActive ? 'Aberto' : 'Encerrado',
                    },
                }));

                setEvents(nextEvents);

                if (nextEvents.length > 0) {
                    setSelectedEventId(nextEvents[0].id);
                }
            } catch (error) {
                setStateAlert({
                    status: true,
                    type: 'error',
                    msg: 'Não foi possível carregar os eventos liberados.',
                });
            } finally {
                setLoadingEvents(false);
            }
        }

        loadAllowedEvents();
    }, [allowedEventIds]);

    useEffect(() => {
        async function loadSubscribers() {
            if (!selectedEventId) {
                setSubscribers([]);
                return;
            }

            try {
                setLoadingSubscribers(true);
                const orders = await fetchAllOrdersByEvent(selectedEventId);
                setSubscribers(buildSubscribersFromOrders(orders));
            } catch (error) {
                setSubscribers([]);
                setStateAlert({
                    status: true,
                    type: 'error',
                    msg: 'Não foi possível carregar os inscritos do evento selecionado.',
                });
            } finally {
                setLoadingSubscribers(false);
            }
        }

        loadSubscribers();
    }, [selectedEventId]);

    async function handleExportExcel() {
        if (!selectedEvent) {
            setStateAlert({ status: true, type: 'warning', msg: 'Selecione um evento para exportar.' });
            return;
        }

        if (subscribers.length === 0) {
            setStateAlert({ status: true, type: 'warning', msg: 'Não há inscritos para exportar.' });
            return;
        }

        try {
            setExporting(true);

            const sheetData = subscribers.map((subscriber) => ({
                Evento: subscriber.attributes.event,
                Instituicao: subscriber.attributes.company,
                Cidade: subscriber.attributes.locale,
                UF: subscriber.attributes.uf,
                Nome: subscriber.attributes.name,
                Cracha: subscriber.attributes.badge,
                Email: subscriber.attributes.email,
                Whatsapp: subscriber.attributes.whatsapp,
                CPF: subscriber.attributes.document,
            }));

            const worksheet = XLSX.utils.json_to_sheet(sheetData);
            const workbook = XLSX.utils.book_new();
            const sheetName = String(selectedEvent.attributes?.name || 'inscritos').slice(0, 31);

            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
            XLSX.writeFile(
                workbook,
                `portal-eventos-${String(selectedEvent.attributes?.name || 'inscritos').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.xlsx`
            );
        } catch (error) {
            setStateAlert({ status: true, type: 'error', msg: 'Não foi possível gerar o Excel.' });
        } finally {
            setExporting(false);
        }
    }

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', p: { xs: 2, md: 4 }, background: 'linear-gradient(180deg, #f4f7fb 0%, #eef3f8 100%)' }}>
            <Snackbar
                open={stateAlert.status}
                autoHideDuration={2500}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => setStateAlert({ status: false, type: 'success', msg: '' })}
            >
                <Alert severity={stateAlert.type} variant="filled" sx={{ width: '100%' }}>
                    {stateAlert.msg}
                </Alert>
            </Snackbar>

            <Stack spacing={3}>
                <Paper elevation={0} sx={{ borderRadius: 5, overflow: 'hidden', border: '1px solid rgba(15, 23, 42, 0.08)' }}>
                    <Box sx={{ px: { xs: 3, md: 4 }, py: { xs: 3, md: 4 }, background: 'radial-gradient(circle at top left, rgba(26, 115, 232, 0.18), transparent 35%), linear-gradient(135deg, #ffffff 0%, #eef4fb 100%)' }}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={7}>
                                <Typography variant="overline" sx={{ color: '#1A73E8', fontWeight: 700, letterSpacing: '0.18em' }}>
                                    CONSULTA 
                                </Typography>
                                <Typography variant="h4" sx={{ color: '#162033', fontWeight: 700, mt: 1 }}>
                                    Eventos e inscritos liberados para este usuário
                                </Typography>
                                <Typography sx={{ color: '#516079', mt: 1.5, maxWidth: 720 }}>
                                    Esta área foi reduzida para consulta. Os eventos exibidos aqui são definidos pelo administrador e o relatório em Excel respeita exatamente essa liberação.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Autocomplete
                                    options={events}
                                    loading={loadingEvents}
                                    getOptionLabel={(option) => option.attributes?.name || ''}
                                    value={selectedEvent}
                                    onChange={(_, newValue) => setSelectedEventId(newValue?.id || null)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Selecionar evento"
                                            placeholder="Escolha um evento liberado"
                                        />
                                    )}
                                    noOptionsText="Nenhum evento liberado"
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ ...summaryCardSx, background: 'linear-gradient(135deg, #1a365d 0%, #1f5ea8 100%)' }}>
                            <EventNoteIcon sx={{ fontSize: 34, opacity: 0.9 }} />
                            <Box>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>Eventos liberados</Typography>
                                <Typography variant="h3" sx={{ fontWeight: 700 }}>{events.length}</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ ...summaryCardSx, background: 'linear-gradient(135deg, #0f766e 0%, #19a28f 100%)' }}>
                            <GroupsIcon sx={{ fontSize: 34, opacity: 0.9 }} />
                            <Box>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>Inscritos do evento selecionado</Typography>
                                <Typography variant="h3" sx={{ fontWeight: 700 }}>{subscribers.length}</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ ...summaryCardSx, background: 'linear-gradient(135deg, #7c2d12 0%, #d97706 100%)' }}>
                            <TravelExploreIcon sx={{ fontSize: 34, opacity: 0.9 }} />
                            <Box>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>Evento em foco</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {selectedEvent?.attributes?.name || 'Nenhum evento selecionado'}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                                    {selectedEvent?.attributes?.isActive || 'Sem status'}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {allowedEventIds.length === 0 && !loadingEvents ? (
                    <Alert severity="warning" variant="filled">
                        Este usuário ainda não possui eventos liberados para consulta.
                    </Alert>
                ) : null}

                

                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
                    <Box>
                        <Typography variant="h5" sx={{ color: '#162033', fontWeight: 700 }}>
                            Inscritos
                        </Typography>
                        <Typography sx={{ color: '#516079' }}>
                            A listagem abaixo acompanha o evento selecionado acima.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleExportExcel}
                        disabled={exporting || !selectedEvent || loadingSubscribers}
                        startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                    >
                        {exporting ? 'Exportando...' : 'Gerar Excel'}
                    </Button>
                </Stack>

                {loadingSubscribers ? (
                    <Box sx={{ width: '100%', py: 8, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={42} />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TableInfo
                                title={`Inscritos${selectedEvent?.attributes?.name ? ` - ${selectedEvent.attributes.name}` : ''}`}
                                variant="info"
                                rows={subscribers}
                                columns={subscriberColumns}
                            />
                        </Grid>
                    </Grid>
                )}
            </Stack>
        </Box>
    );
}